import {
    BILLING_INTERVAL,
    BILLING_INTERVAL_SCHEMA,
    BILLING_TYPE,
    BILLING_TYPE_SCHEMA,
    ORDER_ORGANIZATION_ROLE_SCHEMA,
    ORDER_ORGANIZATION_TRANSACTIONAL_ROLE_SCHEMA,
} from '@schuettflix/interfaces';
import { LineItem } from './generateLineItemsByProductConfigurations';
import { resolveOrganizationRoles } from './organization-roles';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { z } from 'zod';

export const PaymentOrganizationSchema = z.object({
    orderId: z.string().uuid(),
    organizationId: z.number().int().positive(),
    productId: z.string().uuid(),
    paymentTermId: z.number().positive().nullable(),
    billingMethod: BILLING_TYPE_SCHEMA,
    billingText: z.string().nullish(),
    billingInterval: BILLING_INTERVAL_SCHEMA,
    transactionalRole: ORDER_ORGANIZATION_TRANSACTIONAL_ROLE_SCHEMA,
    functionalRole: ORDER_ORGANIZATION_ROLE_SCHEMA,
});

export type GeneratedPaymentOrganization = Omit<z.infer<typeof PaymentOrganizationSchema>, 'billingText'> & {
    billingText: string | null;
};

type FinanceSettingsV2 = {
    debtorOrganizationId: number;
    creditorOrganizationId: number;
    billingMethod: BILLING_TYPE;
    paymentTermId: number | null;
    billingText?: string | null;
    billingInterval: BILLING_INTERVAL;
};

export type GeneratePaymentOrganizationsParams = {
    orderOrPositionId: string;
    financeSettings: FinanceSettingsV2[];
    platformOrganizationId: number;
    customerOrganizationId: number;
    lineItems: LineItem[];
    onUnmatchedLineItem?: 'throw' | 'skip';
};

/**
 * Deduped first-wins on the `payment_organizations` primary key. A customer-as-partner product with a
 * negative sales price resolves both legs to the same key, and `order-fulfillment` rejects duplicates.
 */
export function generatePaymentOrganizations({
    orderOrPositionId,
    financeSettings,
    platformOrganizationId,
    customerOrganizationId,
    lineItems,
    onUnmatchedLineItem = 'throw',
}: GeneratePaymentOrganizationsParams): GeneratedPaymentOrganization[] {
    const byPrimaryKey = new Map<string, GeneratedPaymentOrganization>();

    for (const lineItem of lineItems) {
        const productCategory = getProductCategoryByProductType(lineItem.productType);
        const { creditorOrganizationId, debtorOrganizationId } = lineItem;

        if (creditorOrganizationId === 'TRANSPORT_EXCHANGE' || debtorOrganizationId === 'TRANSPORT_EXCHANGE') {
            continue;
        }

        const financeSetting = financeSettings.find(
            financeSetting =>
                financeSetting.debtorOrganizationId === debtorOrganizationId &&
                financeSetting.creditorOrganizationId === creditorOrganizationId
        );

        if (!lineItem.productConfigurationId || !financeSetting) {
            if (onUnmatchedLineItem === 'skip') {
                continue;
            }

            throw new Error(
                lineItem.productConfigurationId
                    ? `Could not match a finance setting that would belong to the line item with debtorOrganizationId: ${debtorOrganizationId} and creditorOrganizationId: ${creditorOrganizationId}. This should not happen.`
                    : `The line item with debtorOrganizationId: ${debtorOrganizationId} and creditorOrganizationId: ${creditorOrganizationId} is missing a productConfigurationId.`
            );
        }

        const { functionalRoles, transactionalRole } = resolveOrganizationRoles({
            orderingOrganizationId: customerOrganizationId,
            sflxPlatformId: platformOrganizationId,
            productCategory,
            creditorOrganizationId,
            debtorOrganizationId,
        });

        const paymentOrganization: GeneratedPaymentOrganization = {
            orderId: orderOrPositionId,
            productId: lineItem.productConfigurationId,
            organizationId:
                creditorOrganizationId === platformOrganizationId ? debtorOrganizationId : creditorOrganizationId,
            transactionalRole,
            functionalRole: functionalRoles[0]!,
            paymentTermId: financeSetting.paymentTermId ?? null,
            billingMethod: financeSetting.billingMethod,
            billingText: financeSetting.billingText ?? null,
            billingInterval: financeSetting.billingInterval,
        };

        const key = [
            paymentOrganization.productId,
            paymentOrganization.organizationId,
            paymentOrganization.transactionalRole,
            paymentOrganization.functionalRole,
        ].join('|');

        if (!byPrimaryKey.has(key)) {
            byPrimaryKey.set(key, paymentOrganization);
        }
    }

    return [...byPrimaryKey.values()];
}
